/* eslint-disable @typescript-eslint/naming-convention */
import ApiService from 'config/apiServiceApiGateway'
import FileService from 'config/apiServiceFile'
import { buildQueryParams } from 'utils/helpers/queryParams'
import { LocationType } from 'features/Location/@types/location'
import type {
  PurchaseOrderDetailsInterface,
  PurchaseOrderInterface,
  ReasonType,
} from '../@types/PurchaseOrder'
import { purchaseOrderDetailRolesRBAC } from '../pages/PurchaseOrderDetail/rbac'

export interface GetPurchaseOrderRequestParamsInterface {
  page_size: number
  cursor?: string
  status?: string[]
  location_types?: LocationType[]
  destination_ids?: number[]
  po_number?: string
  vendor_ids?: number[]
  date?: string
  req_shipping_date_from?: string
  req_shipping_date_to?: string
  sort?: string
  direction?: string
  include_all?: boolean
}

export interface GetPurchaseOrderResponseInterface {
  pagination: {
    prev_cursor: string
    next_cursor: string
  }
  data: PurchaseOrderInterface[]
  error: {
    message: string
    code: number
    status: boolean
  }
}

interface DefaultNoDataResponseInterface {
  message: string
  error: {
    status: boolean
    message: string
    code: number
  }
}

export interface PutUpdateStatusPurchaseOrderRequestPayloadInterface {
  status: 'ARRIVED' | 'APPROVE' | 'REJECTED' | 'RECEIVING'
}

export interface GetPurchaseOrderDetailsResponseCustomInterface {
  data: PurchaseOrderDetailsInterface
  error: {
    message: string
    code: number
    status: boolean
  }
  // --- backward compatibility
  tags?: {
    id: number
    is_highlighted: boolean
    label: string
  }[]
  remarks?: string[]
  inbound_schedule?: {
    booking_type: string
    timeslot_type: string
    arrival_time: string
  }
}

export interface GetPurchaseOrderReasonsRequestParamsInterface {
  location_id: number
  type: 'CANCEL' | 'PARTIAL_COMPLETE'
}

export interface PostCancelPurchaseOrderPayloadInterface {
  purchase_order_reason_id: number
}

export const getPurchaseOrderApi = (
  queryParams: GetPurchaseOrderRequestParamsInterface,
  RBACPageID?: string,
) =>
  ApiService.Get<GetPurchaseOrderResponseInterface, true>({
    url: '/commercial/internal/v1/purchase-orders',
    params: queryParams,
    config: {
      paramsSerializer: {
        serialize: (query) => buildQueryParams(query).toString(),
      },
      headers: RBACPageID
        ? {
            'x-page-id': RBACPageID,
          }
        : undefined,
    },
  })

export const putUpdateStatusPurchaseOrderApi = (
  purchaseOrderId: number,
  payload: PutUpdateStatusPurchaseOrderRequestPayloadInterface,
) =>
  ApiService.Put<DefaultNoDataResponseInterface, true>({
    url: '/commercial/internal/v1/purchase-orders/:purchaseOrderId/status',
    data: payload,
    urlParams: { purchaseOrderId },
  })

export const getPurchaseOrderDetailApi = (purchaseOrderId: number) =>
  ApiService.Get<GetPurchaseOrderDetailsResponseCustomInterface, true>({
    url: '/commercial/internal/:version/purchase-orders/:purchaseOrderId',
    version: 'v1',
    urlParams: { purchaseOrderId },
    config: {
      headers: {
        'x-page-id': purchaseOrderDetailRolesRBAC.pageID,
      },
    },
  })

export const getPurchaseOrderReasonsApi = (params: GetPurchaseOrderReasonsRequestParamsInterface) =>
  ApiService.Get<ReasonType[]>({
    url: '/commercial/internal/v1/purchase-order-reasons',
    params,
  })

export const postCancelPurchaseOrderApi = (
  purchaseOrderId: number,
  payload: PostCancelPurchaseOrderPayloadInterface,
) =>
  ApiService.Post<DefaultNoDataResponseInterface, true>({
    url: '/commercial/internal/v1/purchase-orders/:purchaseOrderId/cancellation',
    data: payload,
    urlParams: { purchaseOrderId },
  })

export interface ValidatePurchaseOrderCompletePayloadInterface {
  package_label_id: number
}

export interface ValidatePurchaseOrderCompleteResponseInterface {
  purchase_order_id: number
  is_completed: boolean
}

export type UploadGrnOrDeliveryNoteResponseType = {
  data: {
    filePath: string
    fileName: string
    fileUrl: string
  }
}

export type PurchaseOrderDocumentType = 'SURAT_JALAN' | 'GRN'

export type SubmitDocumentPayloadType = {
  documents: {
    type: PurchaseOrderDocumentType
    file_url: string
  }[]
}

export type PurchaseOrderDocumentDataType = {
  id: number
  purchase_order_id: number
  type: string
  file_url: string
  created_at: string
  created_by: string
}

export type GetDocumentApiResponseType = {
  documents: PurchaseOrderDocumentDataType[]
}

export const validatePurchaseOrderComplete = (
  data: ValidatePurchaseOrderCompletePayloadInterface,
) =>
  ApiService.Post<ValidatePurchaseOrderCompleteResponseInterface>({
    url: '/wims/internal/v1/purchase-order-inbounds:validateComplete',
    data,
  })

export const uploadFileGrnOrDeliveryNoteApi = (data: FormData) =>
  FileService.Post<UploadGrnOrDeliveryNoteResponseType, true>({
    url: '/internal/v1/commercial-purchase-order',
    data,
    contentType: 'multipart/form-data',
  })

export const submitDocumentApi = (purchaseOrderId: number, payload: SubmitDocumentPayloadType) =>
  ApiService.Post<DefaultNoDataResponseInterface, true>({
    url: '/commercial/internal/v1/purchase-orders/:purchaseOrderId/documents',
    data: payload,
    urlParams: { purchaseOrderId },
  })

export const getDocumentPoDetailApi = (purchaseOrderId: number) =>
  ApiService.Get<GetDocumentApiResponseType, true>({
    url: `/commercial/internal/v1/purchase-orders/${purchaseOrderId}/documents`,
  })

export const deletePoDetailDocument = (purchaseOrderId: number, documentId: number) =>
  ApiService.Delete<DefaultNoDataResponseInterface, true>({
    url: '/commercial/internal/v1/purchase-orders/:purchaseOrderId/documents/:documentId',
    urlParams: { purchaseOrderId, documentId },
  })

export const getDownloadPoDocument = (fileUrl: string) =>
  FileService.Get<Blob, true>({
    url: '/internal/v1/commercial-purchase-order/download',
    params: { fileUrl },
    config: {
      responseType: 'blob',
      transformResponse: (data) => data,
    },
  })
